/**
 * A ficha do associado na tela (spec 031, passo 2 do §9): o cartão do
 * detalhe, o formulário do "Novo ticket", a edição, a dica do telefone, o
 * destino da cotação e a ficha do portal.
 *
 * PURO de propósito (sem React e sem alias `@/`): o web não tem executor de
 * testes, e a prova mora na API (apps/api/src/shared/ticket-sheet-view.test.ts),
 * como a do description-edit e a do event-detail.
 *
 * Quem decide a ficha é a MESMA resolveTicketSheet do @erp/shared que o
 * disparo, a OS e o bot usam — aqui ela roda no navegador, com o ticket como a
 * API o devolve. Contra uma API anterior à spec 031 (o SPA sobe antes: 3 min
 * contra 25) o detalhe não traz `sheet`, e a ficha é a de ninguém: a tela lê a
 * descrição, como antes.
 */
import {
  EMPTY_TICKET_SHEET,
  TICKET_SHEET_MAX,
  TICKET_SHEET_SLOTS,
  isDispatchSheetErrorCode,
  isStubPhone,
  manualTicketSheetProblem,
  parseSgrDescription,
  resolveTicketSheet,
  sgrSheetEntries,
  ticketSheetSlotLabel,
  toNationalPhone,
  type DispatchSheetErrorCode,
  type DispatchSheetRequirement,
  type PublicTicketSheet,
  type ResolvedTicketSheet,
  type TicketSheetSlot,
  type TicketSheetSlots,
  type TicketSheetSource,
  type Terms,
} from '@erp/shared';

type SheetTerms = Pick<Terms, 'contact' | 'resourceId' | 'resource'>;

// ── os blocos do formulário (§5) ────────────────────────────────────────────

/** à vista: nome, telefone, telefone adicional e o identificador do recurso */
export const SHEET_MAIN_SLOTS = ['name', 'phone', 'phoneAlt', 'plate'] as const satisfies readonly TicketSheetSlot[];
/** em "Mais dados da ficha": o recurso e o endereço */
export const SHEET_MORE_SLOTS = [
  'brand',
  'model',
  'color',
  'year',
  'chassis',
  'street',
  'number',
  'complement',
  'neighborhood',
  'city',
  'state',
  'zip',
] as const satisfies readonly TicketSheetSlot[];

/** o tamanho de cada campo (o mesmo do schema da API e dos CHECKs do banco) */
export const sheetMaxLength = (slot: TicketSheetSlot): number => TICKET_SHEET_MAX[slot];

/** o rótulo do campo, no vocabulário do tenant */
export const sheetLabel = (slot: TicketSheetSlot, terms: SheetTerms): string => ticketSheetSlotLabel(slot, terms);

/** o título do bloco: "Ficha do associado" na PoolTrack, "Ficha do contato" no genérico */
export const sheetTitle = (terms: Pick<Terms, 'contact'>): string => `Ficha do ${terms.contact}`;

// ── a ficha do ticket ───────────────────────────────────────────────────────

/** O ticket como a tela o tem (o TicketDetail da API) — `sheet` pode faltar na API velha. */
export interface SheetTicket {
  title: string;
  description: string | null;
  createdVia: string;
  requestorName: string | null;
  requestorPhone: string | null;
  equipment: { name: string } | null;
  sheet?: TicketSheetSlots | null;
  sheetEditedAt?: string | Date | null;
  sheetEditedBy?: { name: string } | null;
  /**
   * A foto do SGR (spec 032, §2.5): o que o SGR dizia quando alguém escolheu o
   * veículo. Ausente na API anterior à 032.
   */
  sheetFromSgr?: {
    vehicleSgrId: string;
    at: string | Date;
    by: { name: string } | null;
    /** a foto é da rotina noturna (spec 035, D4) — sem pessoa; ausente na API anterior */
    routine?: boolean;
    slots: TicketSheetSlots;
  } | null;
  /**
   * O SGR mudou desde a foto (spec 035, D5): os slots em que o registro atual
   * do veículo difere dela. null/ausente = sem com o que comparar.
   */
  sgrDrift?: { slots: string[]; readAt: string | Date | null } | null;
}

/** A ficha guardada; na API anterior à spec 031, a de ninguém. */
export function sheetOfTicket(ticket: Pick<SheetTicket, 'sheet'>): TicketSheetSlots {
  return ticket.sheet ? { ...EMPTY_TICKET_SHEET, ...ticket.sheet } : { ...EMPTY_TICKET_SHEET };
}

/** resolveTicketSheet com o ticket da tela */
export function resolveSheetOf(ticket: SheetTicket): ResolvedTicketSheet {
  return resolveTicketSheet({
    title: ticket.title,
    description: ticket.description,
    createdVia: ticket.createdVia,
    requestorName: ticket.requestorName,
    requestorPhone: ticket.requestorPhone,
    equipmentName: ticket.equipment?.name ?? null,
    // spec 032: o "Código do cadastro" do ticket aberto pela placa (API anterior: null)
    sgrVehicleId: ticket.sheetFromSgr?.vehicleSgrId ?? null,
    sheet: sheetOfTicket(ticket),
  });
}

/** "da descrição", "do título"… — de onde veio o valor, como o cartão diz */
const SOURCE_TEXT: Record<Exclude<TicketSheetSource, 'person'>, string> = {
  title: 'do título',
  description: 'da descrição',
  requestor: 'do solicitante',
  equipment: 'do recurso',
};
/** "Na descrição: X" — o que a origem diz, ao lado do valor que a pessoa escreveu */
const FALLBACK_PREFIX: Record<Exclude<TicketSheetSource, 'person'>, string> = {
  title: 'No título',
  description: 'Na descrição',
  requestor: 'No solicitante',
  equipment: 'No recurso',
};

/** "06/10" em Brasília — o dia da foto do SGR no rótulo do cartão */
export function sheetPhotoDay(at: string | Date): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit' }).format(d);
}

/** "05/10/2026 14:32" em Brasília — sem depender do relógio de quem vê */
export function sheetEditedWhen(at: string | Date): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
    .format(d)
    .replace(',', '');
}

export interface SheetCardRow {
  slot: TicketSheetSlot;
  label: string;
  /** o valor efetivo; null = vazio (ou apagado) */
  value: string | null;
  /** "da descrição", "editado por Ana em 05/10/2026 14:32"…; null quando vazio */
  origin: string | null;
  /** a pessoa escreveu algo que a origem diz diferente: "Na descrição: X" */
  differs: string | null;
  /** a pessoa escreveu neste campo (o "voltar ao da descrição" faz sentido) */
  edited: boolean;
  /**
   * spec 032: o valor efetivo (de qualquer camada) é igual à foto do SGR — o
   * rótulo diz "do SGR". Não implica `edited`: no importado, o da descrição também.
   */
  fromSgr: boolean;
  /** spec 032: a foto do SGR diz OUTRO valor — "No SGR: X", com "usar" */
  sgrValue: string | null;
}

export interface SheetCard {
  rows: SheetCardRow[];
  /** só leitura, da descrição: "Código do cadastro", "Cadastrado em" (§1) */
  meta: Array<{ label: string; value: string }>;
  /** tem alguma coisa para mostrar (valor ou edição) */
  filled: boolean;
}

/**
 * O cartão da ficha: os campos à vista sempre (com "—" quando vazios — é o que
 * diz à atendente o que falta); os de "Mais dados" só quando têm valor ou
 * foram editados.
 */
export function sheetCard(ticket: SheetTicket, terms: SheetTerms, sourceName?: string): SheetCard {
  const resolved = resolveSheetOf(ticket);
  const editedBy = ticket.sheetEditedBy?.name ?? null;
  const editedAt = ticket.sheetEditedAt ? sheetEditedWhen(ticket.sheetEditedAt) : '';
  const personOrigin = `editado${editedBy ? ` por ${editedBy}` : ''}${editedAt ? ` em ${editedAt}` : ''}`;
  // spec 032: a foto do SGR — "do SGR (06/10, por Maria)" no slot que ainda é igual a ela
  const photo = ticket.sheetFromSgr ?? null;
  const photoAt = photo ? sheetPhotoDay(photo.at) : '';
  // quem tirou a foto: a pessoa ("por Maria") ou a rotina noturna (spec 035, D4)
  const photoBy = photo?.by?.name ? `por ${photo.by.name}` : photo?.routine ? SGR_ROUTINE_PHOTO_LABEL : '';
  // o nome da origem vem da API (`sourceName`); sem ele, "da origem"
  const sgrOrigin = `${sourceName ? `do ${sourceName}` : 'da origem'}${photoAt || photoBy ? ` (${[photoAt, photoBy].filter(Boolean).join(', ')})` : ''}`;

  const rows: SheetCardRow[] = [];
  for (const slot of TICKET_SHEET_SLOTS) {
    const r = resolved.slots[slot];
    const edited = r.source === 'person';
    const main = (SHEET_MAIN_SLOTS as readonly string[]).includes(slot);
    // fora dos campos à vista: só com valor, editado ou com o que a foto do SGR diz
    if (!main && r.value === null && !edited && !photo?.slots[slot]) continue;
    const fb = r.fallback;
    const differs =
      edited && fb.value !== null && fb.source !== null && fb.source !== 'person' && fb.value !== r.value
        ? `${FALLBACK_PREFIX[fb.source]}: ${fb.value}`
        : null;
    const photoValue = photo?.slots[slot] ?? null;
    // o valor EFETIVO, de qualquer camada: no importado, o slot que a descrição
    // traz igual à foto também é "do SGR" (§5) — a mesma régua do "Atualizar do SGR"
    const fromSgr = r.value !== null && photoValue !== null && r.value === photoValue;
    rows.push({
      slot,
      label: sheetLabel(slot, terms),
      value: r.value,
      origin: fromSgr
        ? sgrOrigin
        : edited
          ? personOrigin
          : r.source !== null && r.source !== 'person'
            ? SOURCE_TEXT[r.source]
            : null,
      differs,
      edited,
      fromSgr,
      sgrValue: photoValue !== null && photoValue !== '' && photoValue !== r.value ? photoValue : null,
    });
  }
  const meta: Array<{ label: string; value: string }> = [];
  if (resolved.registeredAt) meta.push({ label: 'Cadastrado em', value: resolved.registeredAt });
  if (resolved.sgrId) meta.push({ label: 'Código do cadastro', value: resolved.sgrId });
  return { rows, meta, filled: rows.some((r) => r.value !== null || r.edited) || meta.length > 0 };
}

/** quem tirou a foto quando não foi uma pessoa: "do SGR (07/10, rotina diária)" */
export const SGR_ROUTINE_PHOTO_LABEL = 'rotina diária';

/**
 * O aviso do cartão quando o SGR mudou desde a foto (spec 035, D5): "O SGR
 * mudou 2 campos desde 07/10: Telefone do associado, Cidade." O dia é o da
 * FOTO. null = nada mudou (ou não há com o que comparar). O nome da origem vem
 * da API; os rótulos, do vocabulário do tenant.
 */
export function sgrDriftText(
  ticket: Pick<SheetTicket, 'sheetFromSgr' | 'sgrDrift'>,
  sourceName: string,
  label?: (slot: TicketSheetSlot) => string
): string | null {
  const photo = ticket.sheetFromSgr ?? null;
  const drift = ticket.sgrDrift ?? null;
  if (!photo || !drift || drift.slots.length === 0) return null;
  const n = drift.slots.length;
  const day = sheetPhotoDay(photo.at);
  const who = sourceName ? `O ${sourceName}` : 'A origem';
  const which = label ? `: ${drift.slots.map((s) => label(s as TicketSheetSlot)).join(', ')}` : '';
  return `${who} mudou ${n === 1 ? '1 campo' : `${n} campos`}${day ? ` desde ${day}` : ''}${which}.`;
}

// ── a edição do cartão ──────────────────────────────────────────────────────

/** O que o formulário de edição abre: o valor EFETIVO de cada campo. */
export function sheetEditDraft(ticket: SheetTicket): Record<TicketSheetSlot, string> {
  const resolved = resolveSheetOf(ticket);
  const out = {} as Record<TicketSheetSlot, string>;
  for (const slot of TICKET_SHEET_SLOTS) out[slot] = resolved.slots[slot].value ?? '';
  return out;
}

const normalizeSlot = (slot: TicketSheetSlot, value: string): string => {
  const v = value.trim();
  return slot === 'state' ? v.toUpperCase() : v;
};

/**
 * O corpo do PATCH da edição, ou null quando nada mudou.
 *
 * - "voltar ao da descrição" (`reset`): o slot vai a null (segue a origem) —
 *   só se a pessoa tinha escrito nele;
 * - o campo igual ao que o formulário abriu: nada muda (escrever na ficha o
 *   mesmo que a descrição diz não é edição);
 * - diferente: o que se digitou; vazio é `''` — a pessoa apagou, e vence a
 *   descrição (o telefone apagado sai do disparo, da busca e do bot).
 */
export function sheetEditPatch(
  ticket: SheetTicket,
  draft: Record<TicketSheetSlot, string>,
  reset: ReadonlySet<TicketSheetSlot>
): { sheet: Partial<TicketSheetSlots> } | null {
  const stored = sheetOfTicket(ticket);
  const opened = sheetEditDraft(ticket);
  const sheet: Partial<TicketSheetSlots> = {};
  for (const slot of TICKET_SHEET_SLOTS) {
    if (reset.has(slot)) {
      if (stored[slot] !== null) sheet[slot] = null;
      continue;
    }
    const value = normalizeSlot(slot, draft[slot] ?? '');
    if (value === normalizeSlot(slot, opened[slot])) continue;
    if (value !== stored[slot]) sheet[slot] = value;
  }
  return Object.keys(sheet).length > 0 ? { sheet } : null;
}

// ── o "Novo ticket" ─────────────────────────────────────────────────────────

export const emptySheetDraft = (): Record<TicketSheetSlot, string> => {
  const out = {} as Record<TicketSheetSlot, string>;
  for (const slot of TICKET_SHEET_SLOTS) out[slot] = '';
  return out;
};

/**
 * A ficha do POST: campo vazio vai `null` (segue o resto da ordem), nunca
 * `''` (§5). Nada preenchido = sem ficha (undefined).
 */
export function newTicketSheet(draft: Record<TicketSheetSlot, string>): Partial<TicketSheetSlots> | undefined {
  const sheet: Partial<TicketSheetSlots> = {};
  let any = false;
  for (const slot of TICKET_SHEET_SLOTS) {
    const value = normalizeSlot(slot, draft[slot] ?? '');
    sheet[slot] = value === '' ? null : value;
    if (value !== '') any = true;
  }
  return any ? sheet : undefined;
}

// ── o telefone: a mesma régua do disparo ────────────────────────────────────

export type PhoneHint = 'ok' | 'landline' | 'invalid';

/**
 * O disparo aceita o formato? A MESMA conta do envio (toNationalPhone +
 * isStubPhone, o sendablePhone do disparo): DDD + 8 ou 9 dígitos, sem número
 * de enchimento. É só o que se sabe — o formato, não se o número tem WhatsApp
 * (revisão do coordenador, 05/10): DDD + 8 dígitos que não começam com 9 é um
 * fixo, aceito, mas com o aviso para conferir. Vazio não tem dica.
 */
export function phoneHint(raw: string): PhoneHint | null {
  if (raw.trim() === '') return null;
  const national = toNationalPhone(raw);
  if (isStubPhone(national)) return 'invalid';
  return national.length === 10 && national[2] !== '9' ? 'landline' : 'ok';
}

export const PHONE_HINT_TEXT: Record<PhoneHint, string> = {
  ok: 'formato aceito pelo disparo',
  landline: 'formato aceito pelo disparo · parece fixo — confira se tem WhatsApp',
  invalid: 'fora do formato (DDD + número)',
};

// ── a cotação: o destino é o endereço efetivo ───────────────────────────────

/** "Rua X, 123 - Bairro, Cidade - UF, CEP" — o endereço da ficha (o editado vence). */
export function destinationOf(ticket: SheetTicket): string {
  const s = resolveSheetOf(ticket).slots;
  const street = [s.street.value, s.number.value].filter(Boolean).join(', ');
  const place = [s.city.value, s.state.value].filter(Boolean).join(' - ');
  return [[street, s.neighborhood.value].filter(Boolean).join(' - '), place, s.zip.value]
    .filter((part): part is string => !!part && part.trim() !== '')
    .join(', ');
}

// ── o portal ────────────────────────────────────────────────────────────────

/**
 * O que o portal mostra do ticket:
 * - a API manda `sheet` (spec 031): a ficha dela (a correção da equipe, nunca
 *   o solicitante); a descrição livre aparece abaixo quando NÃO é a própria
 *   ficha do worker;
 * - a API não manda (anterior à spec 031, por rollback): a ficha lida da
 *   descrição, como antes, e a descrição só quando não há ficha.
 */
export function portalSheetView(
  detail: { description: string | null; sheet?: PublicTicketSheet | null },
  terms: Pick<Terms, 'resource' | 'resourceId' | 'contact'>
): { entries: Array<{ label: string; value: string }>; showDescription: boolean } {
  if (detail.sheet === undefined) {
    const entries = sgrSheetEntries(parseSgrDescription(detail.description), terms);
    return { entries, showDescription: entries.length === 0 };
  }
  const entries = detail.sheet ? sgrSheetEntries(detail.sheet, terms) : [];
  const workerSheet = parseSgrDescription(detail.description).recognized;
  return { entries, showDescription: !workerSheet || entries.length === 0 };
}

// ── a trava do disparo (spec 031, D5) ───────────────────────────────────────

/**
 * O texto do 422 da trava como a API o manda (`details.message`, já com a mesa
 * e o vocabulário do tenant), ou null quando o erro não é da trava ou veio sem
 * a mensagem. O errorMessage do SPA o usa — toda tela que edita o ticket
 * (ficha, solicitante, troca de mesa) mostra o mesmo texto.
 */
export function dispatchSheetErrorText(e: unknown): string | null {
  if (!e || typeof e !== 'object') return null;
  const code = (e as { error?: unknown }).error;
  const details = (e as { details?: unknown }).details;
  if (!isDispatchSheetErrorCode(code) || !details || typeof details !== 'object') return null;
  const text = (details as { message?: unknown }).message;
  return typeof text === 'string' && text.trim() !== '' ? text : null;
}

/** Os campos que a regra de disparo da mesa exige (o asterisco do formulário). */
export function requiredSheetSlots(requirement: DispatchSheetRequirement | null | undefined): Set<TicketSheetSlot> {
  const out = new Set<TicketSheetSlot>();
  if (!requirement) return out;
  if (requirement.phone) out.add('phone');
  if (requirement.name) out.add('name');
  if (requirement.plate) out.add('plate');
  return out;
}

/**
 * O que a API recusaria neste "Novo ticket" (422), julgado ANTES do envio pelo
 * MESMO juiz dela (manualTicketSheetProblem — as funções do disparo), com o
 * ticket como ele vai nascer: a ficha digitada, a descrição, o título, o
 * solicitante e o recurso escolhido.
 */
export function newTicketSheetProblem(input: {
  requirement: DispatchSheetRequirement | null | undefined;
  title: string;
  description: string | null;
  requestorName: string | null;
  requestorPhone: string | null;
  equipmentName: string | null;
  sheet: Record<TicketSheetSlot, string>;
}): DispatchSheetErrorCode | null {
  if (!input.requirement) return null;
  return manualTicketSheetProblem(input.requirement, {
    ticketNumber: 0,
    title: input.title,
    description: input.description,
    clientName: null,
    createdVia: 'web',
    requestorName: input.requestorName,
    requestorPhone: input.requestorPhone,
    equipmentName: input.equipmentName,
    sheet: { ...EMPTY_TICKET_SHEET, ...(newTicketSheet(input.sheet) ?? {}) },
  });
}

/**
 * A mesma trava na EDIÇÃO do cartão: só quando a edição mexe no telefone da
 * ficha (ou no adicional) de um ticket aberto, numa mesa com a regra ligada —
 * e só o telefone (a regra da edição, combinada em 05/10).
 */
export function sheetEditProblem(
  ticket: SheetTicket & { status: { kind: string } },
  body: { sheet: Partial<TicketSheetSlots> } | null,
  requirement: DispatchSheetRequirement | null | undefined
): DispatchSheetErrorCode | null {
  if (!requirement || !body) return null;
  if (ticket.status.kind === 'closed' || ticket.status.kind === 'canceled') return null;
  if (!('phone' in body.sheet) && !('phoneAlt' in body.sheet)) return null;
  return manualTicketSheetProblem(
    requirement,
    {
      ticketNumber: 0,
      title: ticket.title,
      description: ticket.description,
      clientName: null,
      createdVia: ticket.createdVia,
      requestorName: ticket.requestorName,
      requestorPhone: ticket.requestorPhone,
      equipmentName: ticket.equipment?.name ?? null,
      sheet: { ...sheetOfTicket(ticket), ...body.sheet },
    },
    { phone: true, nameAndPlate: false }
  );
}
